'use client';

import { FC } from 'react';
import { useEffect, useState } from 'react';
import { useStore } from 'zustand';
import { useMapStore } from 'stores/map/mapStore';
import { WorkOrderForm } from 'components/form/work-order';
import { PartnerCard } from './partner-button';
import { DispatchOrganization, GeoLocation, MapData } from '../type';
import { retailerStore } from '@/stores/map/retailProductsStore';
import { GoogleMap } from './google-maps/map';
import { twMerge } from 'tailwind-merge';

interface Props {
  isModal?: 'true' | 'false';
}

export interface PartnerStock {
  price: number | undefined | null;
  lowStock: boolean | undefined | null;
  outOfStock: boolean | undefined | null;
  clickThruUrl: string | undefined | null;
}

export interface FormData {
  organizationId: number[];
  organization: DispatchOrganization;
  job: any;
}

export const MapPlusSidebar: FC<Props> = ({ isModal = 'false' }) => {
  const {
    data,
    hasLoaded,
    mapCenter,
    zoom,
    getDispatchData,
    getPartnerData,
    findClosestStore,
    setActiveMarker,
    setSelectedDistance,
    hasSetLocation,
  } = useStore(useMapStore, (state) => ({
    data: state.data,
    distanceOptions: state.distanceOptions,
    hasLoaded: state.hasLoaded,
    mapCenter: state.mapCenter,
    currentPage: state.currentPage,
    zoom: state.zoom,
    getDispatchData: state.getDispatchData,
    getPartnerData: state.getPartnerData,
    setCurrentPage: state.setCurrentPage,
    findClosestStore: state.findClosestStore,
    hasSetLocation: state.hasSetLocation,
    setActiveMarker: state.setActiveMarker,
    setSelectedDistance: state.setSelectedDistance,
  }));

  const { retailerProducts } = retailerStore();

  useEffect(() => {
    getPartnerData(0, 500, []);
    getDispatchData();
  }, []);

  const { partner, radialData } = data;
  const [isOpenForm, setIsOpenForm] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormData>();
  const [stock, setStock] = useState<PartnerStock | undefined>();
  const [selectedCard, setSelectedCard] = useState<number>();

  const onChangeOpen = (item: MapData) => {
    if (!item || !item.attributes.organization) return;

    const partnerOrgs = radialData.inside
      .map((partner) => (partner?.dispatch ? partner.id : undefined))
      .filter((id) => id !== undefined)
      .filter((id) => id !== item.id)
      .slice(0, 2);

    setIsOpenForm((open) => !open);

    const data: FormData = {
      organizationId: [item.id, ...partnerOrgs],
      organization: item.attributes.organization,
      job: item.attributes.job,
    };

    setFormData(data);
  };

  const handleMarkerClick = (location: MapData) => {
    const coords: GeoLocation = {
        lat: location.attributes.latitude,
        lng: location.attributes.longtitude,
      },
      activeMarkerInfo = {
        id: location.id,
        position: coords,
        label: String(location.attributes.title),
        info: location.attributes,
        isDispatch: Boolean(location.dispatch),
        clickThruUrl: stock?.clickThruUrl,
        price: stock?.price,
        lowStock: stock?.lowStock,
        outOfStock: stock?.outOfStock,
      };

    setActiveMarker(activeMarkerInfo);
  };

  const displayPartnerInfoWindow = (idx: number, _stock: PartnerStock) => {
    setSelectedCard(idx);

    _stock && _stock.clickThruUrl ? setStock(_stock) : setStock(undefined);

    const partnerMapData = radialData.inside[idx];
    if (!partnerMapData) return;

    handleMarkerClick(partnerMapData);
  };

  useEffect(() => {
    if (hasLoaded.dispatch && hasLoaded.partners) {
      hasLoaded.initialLoad = false;
      const data = findClosestStore(true);

      if (data.inside.length === 0) {
        setSelectedDistance(null);
      }
    }
  }, [hasLoaded]);

  return (
    <div className='flex flex-col md:flex-row'>
      <div
        id='sidemap'
        className='w-fill h-[calc(100vh-14rem)] max-h-[275px] overflow-y-auto md:max-h-[600px] md:w-[420px]'
      >
        <div className='sticky w-full p-5 text-left'>
          {radialData.inside.length && hasSetLocation() ? (
            <strong>{`${radialData.inside.length} dealer(s) near you`}</strong>
          ) : radialData.inside.length && !hasSetLocation() ? (
            <strong>{`${radialData.inside.length} dealer(s) in all locations`}</strong>
          ) : (
            <strong>
              {!hasLoaded.initialLoad &&
                'To set a radius you must first enter a location'}
            </strong>
          )}
        </div>

        <div className='flex flex-col '>
          {radialData.inside &&
            radialData.inside.map((data, idx) => {
              const lowes = data.attributes.title
                ?.toLocaleLowerCase()
                .includes('lowes');

              const lowesProduct = retailerProducts?.RetailerProducts?.find(
                (prod) => prod.RetailerName === 'Lowes'
              );

              const stock: PartnerStock = {
                price: lowesProduct?.StockPrices[0].Price,
                lowStock: lowesProduct?.StockPrices[0].stockLevelLow,
                outOfStock: lowesProduct?.StockPrices[0].outOfStock,
                clickThruUrl: lowesProduct?.ClickThruUrl,
              };

              return (
                <div
                  key={data.id}
                  onClick={() => displayPartnerInfoWindow(idx, stock)}
                  className={twMerge([
                    'hover:bg-gray-200',
                    'w-fill mb-2 h-[144px] rounded-2xl border border-dashed border-gray-200 p-4 md:w-[400px]',
                    selectedCard === idx &&
                      ' border-solid border-blue-200 bg-blue-50',
                  ])}
                >
                  {lowes ? (
                    <PartnerCard
                      data={data}
                      stock={stock}
                      onChangeOpen={onChangeOpen}
                    />
                  ) : (
                    <PartnerCard data={data} onChangeOpen={onChangeOpen} />
                  )}
                </div>
              );
            })}
        </div>
      </div>

      <GoogleMap
        className='h-[calc(100vh-14rem)] max-h-[600px] grow'
        mapId={'mrcool_store'}
        center={mapCenter.geometry}
        zoom={zoom}
        gestureHandling={'cooperative'}
        streetViewControl={false}
      />
      <WorkOrderForm
        items={formData}
        isOpen={isOpenForm}
        onClose={() => setIsOpenForm(false)}
      />
    </div>
  );
};
